import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { availableCases } from '../data/casesData';
import { convertStageM2ToW, CONVERSION_CONSTANTS } from '../utils/conversionUtils';

const CaseComparison = () => {
  const [metric, setMetric] = useState('baseline');
  const [unit, setUnit] = useState('m2'); // 'm2', 'w', or 'eurw'
  const [costView, setCostView] = useState('incremental'); // 'incremental' or 'cumulative'

  // Color scheme for each manufacturing stage (Becquerel Institute brand colors)
  const colors = {
    polysilicon: '#E78E42',   // Orange
    ingotWafer: '#EAC144',    // Yellow
    cell: '#0A3C80',          // Light navy
    module: '#000755',        // Dark navy
    polysiliconMarket: '#E78E42',   // Orange (will use pattern)
    ingotWaferMarket: '#EAC144',    // Yellow (will use pattern)
    cellMarket: '#0A3C80',          // Light navy (will use pattern)
    moduleMarket: '#000755'         // Dark navy (will use pattern)
  };

  // Calculate market vs MSP percentages for each case
  // Market values are cumulative, so we need to calculate incremental costs
  const calculateMarketPercentage = (caseData) => {
    const results = caseData.results;
    const totalModule = results.moduleMSP ? results.moduleMSP[metric] : results.moduleMarket[metric];

    let marketTotal = 0;
    let previousCumulative = 0;

    // Add polysilicon market (if purchased)
    if (results.polysiliconMarket) {
      marketTotal += results.polysiliconMarket[metric];
      previousCumulative = results.polysiliconMarket[metric];
    }

    // Add ingot/wafer market increment (if purchased)
    if (results.ingotWaferMarket) {
      const increment = results.ingotWaferMarket[metric] - previousCumulative;
      marketTotal += increment;
      previousCumulative = results.ingotWaferMarket[metric];
    }

    // Add cell market increment (if purchased)
    if (results.cellMarket) {
      const increment = results.cellMarket[metric] - previousCumulative;
      marketTotal += increment;
      previousCumulative = results.cellMarket[metric];
    }

    // Add module market increment (if purchased - Case 1)
    if (results.moduleMarket) {
      const increment = results.moduleMarket[metric] - previousCumulative;
      marketTotal += increment;
    }

    const marketPercent = (marketTotal / totalModule) * 100;
    const mspPercent = 100 - marketPercent;

    return marketPercent > 0 ? { market: marketPercent, msp: mspPercent } : null;
  };

  // Prepare data for stacked bar chart
  const prepareChartData = () => {
    return Object.entries(availableCases).map(([caseKey, caseData]) => {
      const results = caseData.results;
      const dataPoint = {
        case: caseKey,
        caseName: caseData.title,
      };

      // Since MSP values are cumulative (for $/m² stages), we need to calculate incremental values
      let previousMSP = 0;

      // Handle market-purchased components first (shown with transparency)
      if (results.polysiliconMarket) {
        dataPoint['Polysilicon (Market)'] = results.polysiliconMarket[metric];
        previousMSP = results.polysiliconMarket[metric];
      }

      if (results.ingotWaferMarket) {
        dataPoint['Ingot/Wafer (Market)'] = results.ingotWaferMarket[metric] - previousMSP;
        previousMSP = results.ingotWaferMarket[metric];
      }

      if (results.cellMarket) {
        dataPoint['Cell (Market)'] = results.cellMarket[metric] - previousMSP;
        previousMSP = results.cellMarket[metric];
      }

      if (results.moduleMarket) {
        dataPoint['Module (Market)'] = results.moduleMarket[metric] - previousMSP;
        previousMSP = results.moduleMarket[metric];
      }

      // Handle manufactured components
      // Polysilicon values are already in $/m² in casesData.js
      if (results.polysiliconMSP) {
        dataPoint.Polysilicon = results.polysiliconMSP[metric];
        previousMSP = results.polysiliconMSP[metric];
      }

      if (results.ingotWaferMSP) {
        dataPoint['Ingot/Wafer'] = results.ingotWaferMSP[metric] - previousMSP;
        previousMSP = results.ingotWaferMSP[metric];
      }

      if (results.cellMSP) {
        dataPoint.Cell = results.cellMSP[metric] - previousMSP;
        previousMSP = results.cellMSP[metric];
      }

      if (results.moduleMSP) {
        // Apply cell packing factor (0.94) when calculating module incremental cost
        // Only 94% of the cell area fits in the module
        const cellCostInModule = results.cellMSP ? results.cellMSP[metric] * CONVERSION_CONSTANTS.cellPackingFactor : previousMSP;

        // Add packing adjustment as a separate visible bar (-6% adjustment)
        if (results.cellMSP) {
          dataPoint['Packing Adj'] = cellCostInModule - results.cellMSP[metric];
        }

        dataPoint.Module = results.moduleMSP[metric] - cellCostInModule;
        previousMSP = results.moduleMSP[metric];
      }

      // Total is the final module cost (either MSP or Market)
      dataPoint.total = results.moduleMSP ? results.moduleMSP[metric] : results.moduleMarket[metric];

      // If unit is W or EUR/W, convert all $/m² values using stage-specific power densities
      if (unit === 'w' || unit === 'eurw') {
        if (dataPoint['Polysilicon (Market)']) dataPoint['Polysilicon (Market)'] = convertStageM2ToW(dataPoint['Polysilicon (Market)'], 'polysilicon');
        if (dataPoint['Ingot/Wafer (Market)']) dataPoint['Ingot/Wafer (Market)'] = convertStageM2ToW(dataPoint['Ingot/Wafer (Market)'], 'ingotWafer');
        if (dataPoint['Cell (Market)']) dataPoint['Cell (Market)'] = convertStageM2ToW(dataPoint['Cell (Market)'], 'cell');
        if (dataPoint['Module (Market)']) dataPoint['Module (Market)'] = convertStageM2ToW(dataPoint['Module (Market)'], 'module');
        if (dataPoint.Polysilicon) dataPoint.Polysilicon = convertStageM2ToW(dataPoint.Polysilicon, 'polysilicon');
        if (dataPoint['Ingot/Wafer']) dataPoint['Ingot/Wafer'] = convertStageM2ToW(dataPoint['Ingot/Wafer'], 'ingotWafer');
        if (dataPoint.Cell) dataPoint.Cell = convertStageM2ToW(dataPoint.Cell, 'cell');
        if (dataPoint['Packing Adj']) dataPoint['Packing Adj'] = convertStageM2ToW(dataPoint['Packing Adj'], 'cell');
        if (dataPoint.Module) dataPoint.Module = convertStageM2ToW(dataPoint.Module, 'module');
        dataPoint.total = convertStageM2ToW(dataPoint.total, 'module');
      }

      // If unit is EUR/W, also convert to EUR
      if (unit === 'eurw') {
        if (dataPoint['Polysilicon (Market)']) dataPoint['Polysilicon (Market)'] *= CONVERSION_CONSTANTS.usdToEur;
        if (dataPoint['Ingot/Wafer (Market)']) dataPoint['Ingot/Wafer (Market)'] *= CONVERSION_CONSTANTS.usdToEur;
        if (dataPoint['Cell (Market)']) dataPoint['Cell (Market)'] *= CONVERSION_CONSTANTS.usdToEur;
        if (dataPoint['Module (Market)']) dataPoint['Module (Market)'] *= CONVERSION_CONSTANTS.usdToEur;
        if (dataPoint.Polysilicon) dataPoint.Polysilicon *= CONVERSION_CONSTANTS.usdToEur;
        if (dataPoint['Ingot/Wafer']) dataPoint['Ingot/Wafer'] *= CONVERSION_CONSTANTS.usdToEur;
        if (dataPoint.Cell) dataPoint.Cell *= CONVERSION_CONSTANTS.usdToEur;
        if (dataPoint['Packing Adj']) dataPoint['Packing Adj'] *= CONVERSION_CONSTANTS.usdToEur;
        if (dataPoint.Module) dataPoint.Module *= CONVERSION_CONSTANTS.usdToEur;
        dataPoint.total *= CONVERSION_CONSTANTS.usdToEur;
      }

      return dataPoint;
    });
  };

  const chartData = prepareChartData();

  // Custom tooltip to show breakdown
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
      const currency = unit === 'eurw' ? '€' : '$';
      const decimals = unit === 'm2' ? 2 : 3;

      return (
        <div className="bg-white p-4 border border-gray-300 rounded shadow-lg">
          <p className="font-semibold mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {currency}{entry.value.toFixed(decimals)}
            </p>
          ))}
          <p className="font-semibold mt-2 pt-2 border-t border-gray-200">
            Total Module Price: {currency}{total.toFixed(decimals)}
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom Legend to control the order from Polysilicon to Module
  const CustomLegend = () => {
    const legendItems = [
      { name: 'Polysilicon (Market)', color: colors.polysiliconMarket, hasPattern: true },
      { name: 'Polysilicon', color: colors.polysilicon, hasPattern: false },
      { name: 'Ingot/Wafer (Market)', color: colors.ingotWaferMarket, hasPattern: true },
      { name: 'Ingot/Wafer', color: colors.ingotWafer, hasPattern: false },
      { name: 'Cell (Market)', color: colors.cellMarket, hasPattern: true },
      { name: 'Cell', color: colors.cell, hasPattern: false },
      { name: 'Packing Adj (-6%)', color: '#FF6B6B', hasPattern: false },
      { name: 'Module (Market)', color: colors.moduleMarket, hasPattern: true },
      { name: 'Module', color: colors.module, hasPattern: false }
    ];

    return (
      <div className="flex flex-wrap justify-center gap-4 pt-2">
        {legendItems.map((item) => (
          <div key={item.name} className="flex items-center gap-2">
            <div
              className="w-4 h-4 rounded border"
              style={{
                backgroundColor: item.hasPattern ? 'transparent' : item.color,
                backgroundImage: item.hasPattern
                  ? `repeating-linear-gradient(45deg, ${item.color}, ${item.color} 2px, transparent 2px, transparent 6px)`
                  : 'none',
                borderColor: item.hasPattern ? item.color : 'transparent',
                borderWidth: item.hasPattern ? '1px' : '0'
              }}
            />
            <span className="text-sm text-gray-700">{item.name}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-xl border border-gray-200 hover:shadow-2xl transition-shadow duration-300">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-2 h-8 rounded-full" style={{ backgroundImage: 'linear-gradient(to bottom, #E78E42, #EAC144)' }}></div>
          <h2 className="text-2xl font-bold" style={{ color: '#000755' }}>Case Comparison</h2>
        </div>
        <p className="text-gray-600 mb-2 text-sm">
          Stacked cost breakdown across all manufacturing stages for each optimization case
        </p>

        {/* Case Overview */}
        <div className="mb-4 p-4 rounded-xl border-2" style={{ backgroundImage: 'linear-gradient(to right, rgba(231, 142, 66, 0.05), rgba(234, 193, 68, 0.05))', borderColor: '#E78E42' }}>
          <div className="flex items-center gap-2 mb-3">
            <svg className="w-5 h-5" style={{ color: '#E78E42' }} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <h4 className="font-bold text-sm" style={{ color: '#000755' }}>Case Overview</h4>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            <div className="flex items-start gap-2 p-2 bg-white rounded">
              <span className="text-orange-600">🏪</span>
              <div>
                <strong className="text-gray-900">Cases 1-4:</strong>
                <span className="text-gray-600 ml-1">Include market-purchased components at low prices (sometimes economically unsustainable)</span>
              </div>
            </div>
            <div className="flex items-start gap-2 p-2 bg-white rounded">
              <span className="text-green-600">✓</span>
              <div>
                <strong className="text-gray-900">Cases 5-7:</strong>
                <span className="text-gray-600 ml-1">Fully integrated European supply chains with sustainable MSP pricing</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
          <span className="text-blue-700 text-sm mt-0.5">ℹ️</span>
          <p className="text-xs text-blue-800">
            <strong>Visual Guide:</strong> Solid colors represent manufactured (MSP) costs. Diagonal stripe patterns indicate market-purchased components. Cases with market prices show a ⚠️ warning badge with the percentage of market-based costs.
          </p>
        </div>

        {/* Metric and Unit selectors */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div className="flex items-center gap-3 p-4 rounded-xl border" style={{ backgroundImage: 'linear-gradient(to right, rgba(231, 142, 66, 0.1), rgba(234, 193, 68, 0.1))', borderColor: '#E78E42' }}>
            <svg className="w-5 h-5" style={{ color: '#0A3C80' }} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 0l-2 2a1 1 0 101.414 1.414L8 10.414l1.293 1.293a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <label className="font-semibold text-gray-700 whitespace-nowrap">Metric:</label>
            <select
              value={metric}
              onChange={(e) => setMetric(e.target.value)}
              className="flex-1 px-3 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 bg-white font-medium text-gray-700 cursor-pointer transition-colors text-sm"
              style={{ borderColor: '#E78E42', '--tw-ring-color': '#E78E42' }}
            >
              <option value="baseline">📊 Baseline</option>
              <option value="percentile10">📉 10th %ile</option>
              <option value="percentile50">📊 50th %ile</option>
              <option value="percentile90">📈 90th %ile</option>
            </select>
          </div>

          <div className="flex items-center gap-3 p-4 rounded-xl border" style={{ backgroundImage: 'linear-gradient(to right, rgba(234, 193, 68, 0.1), rgba(231, 142, 66, 0.1))', borderColor: '#EAC144' }}>
            <svg className="w-5 h-5" style={{ color: '#0A3C80' }} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
            </svg>
            <label className="font-semibold text-gray-700 whitespace-nowrap">Unit:</label>
            <select
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              className="flex-1 px-3 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 bg-white font-medium text-gray-700 cursor-pointer transition-colors text-sm"
              style={{ borderColor: '#EAC144', '--tw-ring-color': '#EAC144' }}
            >
              <option value="m2">💰 USD/m²</option>
              <option value="w">⚡ USD/W</option>
              <option value="eurw">🇪🇺 EUR/W</option>
            </select>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="w-full overflow-x-auto">
        <div className="min-w-[600px]">
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
          {/* Define diagonal stripe patterns for market prices */}
          <defs>
            <pattern id="polysiliconMarketPattern" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="8" stroke={colors.polysiliconMarket} strokeWidth="4" />
            </pattern>
            <pattern id="ingotWaferMarketPattern" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="8" stroke={colors.ingotWaferMarket} strokeWidth="4" />
            </pattern>
            <pattern id="cellMarketPattern" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="8" stroke={colors.cellMarket} strokeWidth="4" />
            </pattern>
            <pattern id="moduleMarketPattern" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="8" stroke={colors.moduleMarket} strokeWidth="4" />
            </pattern>
          </defs>

          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="case"
            angle={-45}
            textAnchor="end"
            height={100}
          />
          <YAxis
            label={{
              value: unit === 'm2' ? 'Cost ($/m²)' : unit === 'w' ? 'Cost ($/W)' : 'Cost (€/W)',
              angle: -90,
              position: 'insideLeft'
            }}
          />
          <Tooltip content={<CustomTooltip />} />

          {/* Stack all values - Market components first (with diagonal pattern), then manufactured */}
          <Bar dataKey="Polysilicon (Market)" stackId="a" fill="url(#polysiliconMarketPattern)" name="Polysilicon (Market)" stroke={colors.polysiliconMarket} strokeWidth={1} />
          <Bar dataKey="Ingot/Wafer (Market)" stackId="a" fill="url(#ingotWaferMarketPattern)" name="Ingot/Wafer (Market)" stroke={colors.ingotWaferMarket} strokeWidth={1} />
          <Bar dataKey="Cell (Market)" stackId="a" fill="url(#cellMarketPattern)" name="Cell (Market)" stroke={colors.cellMarket} strokeWidth={1} />
          <Bar dataKey="Module (Market)" stackId="a" fill="url(#moduleMarketPattern)" name="Module (Market)" stroke={colors.moduleMarket} strokeWidth={1} />
          <Bar dataKey="Polysilicon" stackId="a" fill={colors.polysilicon} />
          <Bar dataKey="Ingot/Wafer" stackId="a" fill={colors.ingotWafer} />
          <Bar dataKey="Cell" stackId="a" fill={colors.cell} />
          <Bar dataKey="Packing Adj" stackId="a" fill="#FF6B6B" />
          <Bar dataKey="Module" stackId="a" fill={colors.module} />
        </BarChart>
      </ResponsiveContainer>

        {/* Custom Legend with correct order */}
        <CustomLegend />
        </div>
      </div>

      {/* Table View Toggle */}
      <div className="mt-6 flex justify-center">
        <div className="inline-flex items-center gap-3 p-4 rounded-xl border" style={{ backgroundImage: 'linear-gradient(to right, rgba(10, 60, 128, 0.1), rgba(0, 7, 85, 0.1))', borderColor: '#0A3C80' }}>
          <svg className="w-5 h-5" style={{ color: '#000755' }} fill="currentColor" viewBox="0 0 20 20">
            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
            <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
          </svg>
          <label className="font-semibold text-gray-700 whitespace-nowrap">Table View:</label>
          <select
            value={costView}
            onChange={(e) => setCostView(e.target.value)}
            className="px-4 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 bg-white font-medium text-gray-700 cursor-pointer transition-colors text-sm"
            style={{ borderColor: '#0A3C80', '--tw-ring-color': '#0A3C80' }}
          >
            <option value="incremental">📊 Incremental Costs</option>
            <option value="cumulative">📈 Cumulative Costs</option>
          </select>
        </div>
      </div>

      {/* Summary table */}
      <div className="mt-6 overflow-x-auto">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">
          {costView === 'incremental' ? 'Incremental Cost Breakdown' : 'Cumulative Cost Breakdown'}
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {costView === 'incremental' ? (
            <>Values shown are <strong>incremental costs</strong> added at each stage. The stacked bar chart shows how these add up to the total Module Price.</>
          ) : (
            <>Values shown are <strong>cumulative costs</strong> at each stage. Each column includes all previous manufacturing steps.</>
          )}
          <span className="block mt-1 text-xs italic text-blue-600">ℹ️ Polysilicon costs (originally in $/kg) are converted to $/m² using consumption rate of 0.595 kg/m²</span>
          <span className="block mt-1 text-xs italic text-orange-600">🏪 <span className="inline-block w-4 h-3 bg-orange-100 border border-orange-200 rounded"></span> Cases 1-4 with market purchases | <span className="inline-block w-4 h-3 bg-green-100 border border-green-200 rounded"></span> Cases 5-7 fully integrated (all MSP)</span>
          {costView === 'incremental' && <span className="block mt-1 text-xs italic text-purple-600">⚙️ Module incremental cost accounts for 0.94 cell packing factor (only 94% of cell area fits in module)</span>}
          {(unit === 'w' || unit === 'eurw') && <span className="block mt-1 text-xs italic">Polysilicon/Ingot/Wafer/Cell converted using 250 Wp/m² (25% cell efficiency); Module using 232.5 Wp/m² (23.25% module efficiency)</span>}
          {unit === 'eurw' && <span className="block mt-1 text-xs italic">Exchange rate: 1 USD = 0.92 EUR</span>}
        </p>
        <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
          <thead style={{ backgroundImage: 'linear-gradient(to right, rgba(231, 142, 66, 0.2), rgba(234, 193, 68, 0.2))' }}>
            <tr>
              <th className="px-4 py-3 text-left font-bold" style={{ color: '#000755' }}>Case</th>
              <th className="px-4 py-3 text-right font-bold" style={{ color: '#000755' }}>Polysilicon<br/><span className="font-normal text-xs">(Mfg/Market{costView === 'incremental' ? ', incr.' : ', cum.'})</span></th>
              <th className="px-4 py-3 text-right font-bold" style={{ color: '#000755' }}>Ingot/Wafer<br/><span className="font-normal text-xs">(Mfg/Market{costView === 'incremental' ? ', incr.' : ', cum.'})</span></th>
              <th className="px-4 py-3 text-right font-bold" style={{ color: '#000755' }}>Cell<br/><span className="font-normal text-xs">(Mfg/Market{costView === 'incremental' ? ', incr.' : ', cum.'})</span></th>
              <th className="px-4 py-3 text-right font-bold" style={{ color: '#000755' }}>Module<br/><span className="font-normal text-xs">({costView === 'incremental' ? 'incr., 0.94×cell' : 'cumulative'})</span></th>
              <th className="px-4 py-3 text-right font-bold" style={{ color: '#0A3C80', backgroundColor: 'rgba(10, 60, 128, 0.1)' }}>Total Module Price<br/><span className="font-normal text-xs">(cumulative {unit === 'm2' ? '$/m²' : unit === 'w' ? '$/W' : '€/W'})</span></th>
            </tr>
          </thead>
          <tbody>
            {chartData.map((row, idx) => {
              const currency = unit === 'eurw' ? '€' : '$';
              const decimals = unit === 'm2' ? 2 : 3;
              const caseData = availableCases[row.case];
              const marketInfo = calculateMarketPercentage(caseData);
              const results = caseData.results;

              // Helper function to convert and format values for display
              const convertValue = (valueM2, stage) => {
                if (!valueM2) return null;
                let converted = valueM2;

                if (unit === 'w' || unit === 'eurw') {
                  converted = convertStageM2ToW(valueM2, stage);
                }

                if (unit === 'eurw') {
                  converted *= CONVERSION_CONSTANTS.usdToEur;
                }

                return converted;
              };

              // Calculate cumulative values for each stage (if costView is 'cumulative')
              let polysilicon_cumulative = null;
              let ingotWafer_cumulative = null;
              let cell_cumulative = null;
              let module_cumulative = null;

              if (costView === 'cumulative') {
                if (results.polysiliconMSP) {
                  polysilicon_cumulative = convertValue(results.polysiliconMSP[metric], 'polysilicon');
                } else if (results.polysiliconMarket) {
                  polysilicon_cumulative = convertValue(results.polysiliconMarket[metric], 'polysilicon');
                }

                if (results.ingotWaferMSP) {
                  ingotWafer_cumulative = convertValue(results.ingotWaferMSP[metric], 'ingotWafer');
                } else if (results.ingotWaferMarket) {
                  ingotWafer_cumulative = convertValue(results.ingotWaferMarket[metric], 'ingotWafer');
                }

                if (results.cellMSP) {
                  cell_cumulative = convertValue(results.cellMSP[metric], 'cell');
                } else if (results.cellMarket) {
                  cell_cumulative = convertValue(results.cellMarket[metric], 'cell');
                }

                if (results.moduleMSP) {
                  module_cumulative = convertValue(results.moduleMSP[metric], 'module');
                } else if (results.moduleMarket) {
                  module_cumulative = convertValue(results.moduleMarket[metric], 'module');
                }
              }

              // Check if case has market purchases
              const hasMarketPurchases = results.polysiliconMarket || results.ingotWaferMarket || results.cellMarket || results.moduleMarket;
              const rowBgClass = hasMarketPurchases
                ? (idx % 2 === 0 ? 'bg-orange-50' : 'bg-orange-100')
                : (idx % 2 === 0 ? 'bg-green-50' : 'bg-green-100');

              return (
                <tr key={idx} className={rowBgClass}>
                  <td className="px-4 py-3 font-semibold text-gray-700">
                    {row.case}
                    {hasMarketPurchases && <span className="ml-2 text-xs text-orange-600">🏪</span>}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700">
                    {costView === 'incremental' ? (
                      row.Polysilicon ? `${currency}${row.Polysilicon.toFixed(decimals)}` :
                      row['Polysilicon (Market)'] ? <span className="italic text-gray-500">{currency}{row['Polysilicon (Market)'].toFixed(decimals)} 🏪</span> : '-'
                    ) : (
                      polysilicon_cumulative !== null ? (
                        results.polysiliconMarket ? <span className="italic text-gray-500">{currency}{polysilicon_cumulative.toFixed(decimals)} 🏪</span> :
                        `${currency}${polysilicon_cumulative.toFixed(decimals)}`
                      ) : '-'
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700">
                    {costView === 'incremental' ? (
                      row['Ingot/Wafer'] ? `${currency}${row['Ingot/Wafer'].toFixed(decimals)}` :
                      row['Ingot/Wafer (Market)'] ? <span className="italic text-gray-500">{currency}{row['Ingot/Wafer (Market)'].toFixed(decimals)} 🏪</span> : '-'
                    ) : (
                      ingotWafer_cumulative !== null ? (
                        results.ingotWaferMarket ? <span className="italic text-gray-500">{currency}{ingotWafer_cumulative.toFixed(decimals)} 🏪</span> :
                        `${currency}${ingotWafer_cumulative.toFixed(decimals)}`
                      ) : '-'
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700">
                    {costView === 'incremental' ? (
                      row.Cell ? `${currency}${row.Cell.toFixed(decimals)}` :
                      row['Cell (Market)'] ? <span className="italic text-gray-500">{currency}{row['Cell (Market)'].toFixed(decimals)} 🏪</span> : '-'
                    ) : (
                      cell_cumulative !== null ? (
                        results.cellMarket ? <span className="italic text-gray-500">{currency}{cell_cumulative.toFixed(decimals)} 🏪</span> :
                        `${currency}${cell_cumulative.toFixed(decimals)}`
                      ) : '-'
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700">
                    {costView === 'incremental' ? (
                      row.Module ? `${currency}${row.Module.toFixed(decimals)}` :
                      row['Module (Market)'] ? <span className="italic text-gray-500">{currency}{row['Module (Market)'].toFixed(decimals)} 🏪</span> : '-'
                    ) : (
                      module_cumulative !== null ? (
                        results.moduleMarket ? <span className="italic text-gray-500">{currency}{module_cumulative.toFixed(decimals)} 🏪</span> :
                        `${currency}${module_cumulative.toFixed(decimals)}`
                      ) : '-'
                    )}
                  </td>
                  <td className="px-0.5 py-3 text-right font-bold" style={{ color: '#0A3C80', backgroundColor: 'rgba(10, 60, 128, 0.1)' }}>
                    <div className="flex items-center justify-end gap-2">
                      {marketInfo && (
                        <span className="text-xs px-1 py-1 rounded bg-orange-100 text-orange-800 font-normal border border-orange-300">
                          ⚠️ {marketInfo.market.toFixed(0)}% from Market
                        </span>
                      )}
                      <span>{currency}{row.total.toFixed(decimals)}</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legend explanation */}
      <div className="mt-6 p-5 border-2 rounded-xl shadow-md" style={{ backgroundImage: 'linear-gradient(to right, rgba(231, 142, 66, 0.1), rgba(234, 193, 68, 0.1))', borderColor: '#E78E42' }}>
        <div className="flex items-center gap-2 mb-3">
          <svg className="w-5 h-5" style={{ color: '#E78E42' }} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <p className="font-bold" style={{ color: '#000755' }}>Understanding the Chart:</p>
        </div>
        <ul className="space-y-2 text-sm text-gray-700">
          <li className="flex items-start gap-2">
            <span className="font-bold mt-0.5" style={{ color: '#0A3C80' }}>•</span>
            <span><strong>MSP values are cumulative:</strong> Cell MSP for example includes Polysilicon + Ingot/Wafer costs</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold mt-0.5" style={{ color: '#E78E42' }}>•</span>
            <span><strong>Chart shows incremental costs:</strong> Each segment represents the value added at that stage</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold mt-0.5" style={{ color: '#000755' }}>•</span>
            <span><strong>Module manufacturing cost:</strong> Accounts for 0.94 cell packing factor (only 94% of module area is covered by cells)</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold mt-0.5" style={{ color: '#EAC144' }}>•</span>
            <span><strong>Market-purchased components (🏪):</strong> Shown with diagonal stripe pattern - these are bought at market price rather than manufactured</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold mt-0.5" style={{ color: '#E78E42' }}>•</span>
            <span><strong>Case 1:</strong> 🏪 All components purchased from market (Polysilicon, Ingot/Wafer, Cell & Module) - no manufacturing</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold mt-0.5" style={{ color: '#0A3C80' }}>•</span>
            <span><strong>Cases 2-3:</strong> 🏪 Polysilicon & 🏪 Ingot/Wafer (market), manufacture Cell & Module</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold mt-0.5" style={{ color: '#000755' }}>•</span>
            <span><strong>Case 4:</strong> 🏪 Polysilicon (market), manufacture Ingot/Wafer, Cell & Module</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold mt-0.5" style={{ color: '#E78E42' }}>•</span>
            <span><strong>Cases 5-7:</strong> Complete supply chain - all MSP (no market purchases)</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default CaseComparison;
